import { ArrowLeft, Grid3X3, Map, Printer, Smartphone } from "lucide-react";

import {
  NUM_DEF_INNINGS,
  POSITIONS,
} from "../../../shared/domain/defenseGenerator";
import {
  buildDefenseFieldLabels,
  buildDefenseTextGrid,
  buildOopNotes,
  defenseCellLabel,
  isOutOfPosition,
  playerName,
} from "../../domain/defensePresentation";
import type {
  DefenseInningDocument,
  PlayerListItem,
} from "../../shared/types/contracts";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button";
import { ButtonGroup } from "@/shared/ui/button-group";
import { FieldDiagram } from "@/shared/ui/field-diagram";

export type DefenseExportView = "mobile" | "grid" | "field";

interface DefenseExportScreenProps {
  teamName: string;
  players: PlayerListItem[];
  preferences: Record<string, string[]>;
  defense: DefenseInningDocument[];
  view: DefenseExportView;
  fieldInning: number;
  onChangeView: (view: DefenseExportView) => void;
  onSelectFieldInning: (inning: number) => void;
  onPrint: () => void;
  onBack: () => void;
}

const viewOptions = [
  { value: "mobile", label: "MOBILE", icon: Smartphone },
  { value: "grid", label: "GRID", icon: Grid3X3 },
  { value: "field", label: "FIELD", icon: Map },
] as const satisfies ReadonlyArray<{
  value: DefenseExportView;
  label: string;
  icon: typeof Smartphone;
}>;

export function DefenseExportScreen({
  teamName,
  players,
  preferences,
  defense,
  view,
  fieldInning,
  onChangeView,
  onSelectFieldInning,
  onPrint,
  onBack,
}: DefenseExportScreenProps) {
  const selectedFieldInning = Math.min(
    fieldInning,
    Math.max(defense.length - 1, 0),
  );
  const nameOf = (id: string) => playerName(players, id);
  const cell = (inning: DefenseInningDocument, position: string) =>
    defenseCellLabel(inning, position, players, preferences);
  const oopNotes = buildOopNotes(defense, players);
  const textGrid = buildDefenseTextGrid(
    defense,
    players,
    preferences,
    POSITIONS,
    NUM_DEF_INNINGS,
  );

  return (
    <article
      className="defense-print-surface min-h-[calc(100svh-5rem)] rounded-[var(--radius-lg)] bg-white p-3 text-slate-950 shadow-[var(--shadow-card)] sm:p-4"
      data-export-view={view}
    >
      <header className="no-print mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-300 pb-3">
        <h1 className="m-0 min-w-0 font-mono text-base font-bold tracking-[0.08em] sm:text-lg">
          {teamName} — DEFENSIVE ROTATION
        </h1>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <ButtonGroup aria-label="Export view">
            {viewOptions.map(({ value, label, icon: Icon }) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={view === value ? "selected" : "outline"}
                onClick={() => onChangeView(value)}
                className={cn(
                  "px-2",
                  view !== value &&
                    "border-slate-300 bg-white text-slate-700 hover:bg-slate-100",
                )}
              >
                <Icon aria-hidden="true" />
                {label}
              </Button>
            ))}
          </ButtonGroup>
          {view !== "mobile" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onPrint}
              className="border-slate-300 bg-white text-slate-800 hover:bg-slate-100"
            >
              <Printer aria-hidden="true" />
              PRINT
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="← BACK"
            onClick={onBack}
            className="border-slate-300 bg-white text-slate-800 hover:bg-slate-100"
          >
            <ArrowLeft aria-hidden="true" />
            BACK
          </Button>
        </div>
      </header>

      {view === "field" ? (
        <FieldExport
          defense={defense}
          players={players}
          preferences={preferences}
          selectedInning={selectedFieldInning}
          nameOf={nameOf}
          onSelectInning={onSelectFieldInning}
        />
      ) : view === "mobile" ? (
        <MobileExport
          defense={defense}
          preferences={preferences}
          nameOf={nameOf}
        />
      ) : (
        <GridExport
          defense={defense}
          preferences={preferences}
          cell={cell}
          nameOf={nameOf}
        />
      )}

      {oopNotes.length > 0 ? (
        <section className="mt-3 text-xs">
          <strong>⚠ Out-of-position notes:</strong>
          {oopNotes.map((note, index) => (
            <div key={index}>· {note}</div>
          ))}
        </section>
      ) : null}

      <label className="no-print mt-3 block text-xs font-bold text-slate-700">
        Copy-paste version (tab-separated, drops straight into Sheets/Excel):
        <textarea
          readOnly
          value={textGrid}
          onFocus={(event) => event.target.select()}
          className="mt-1 h-32 w-full resize-y rounded border border-slate-300 bg-white p-2 font-mono text-xs text-slate-950 outline-none focus:border-slate-600"
        />
      </label>
    </article>
  );
}

function FieldExport({
  defense,
  players,
  preferences,
  selectedInning,
  nameOf,
  onSelectInning,
}: {
  defense: DefenseInningDocument[];
  players: PlayerListItem[];
  preferences: Record<string, string[]>;
  selectedInning: number;
  nameOf: (id: string) => string;
  onSelectInning: (inning: number) => void;
}) {
  const inning = defense[selectedInning];
  const labels = inning
    ? buildDefenseFieldLabels(inning, players, preferences, POSITIONS)
    : [];

  return (
    <section>
      <div className="no-print mb-2 flex gap-0.5 overflow-x-auto pb-1">
        {defense.map((_, index) => (
          <Button
            key={index}
            type="button"
            size="sm"
            variant={index === selectedInning ? "selected" : "outline"}
            onClick={() => onSelectInning(index)}
            className={cn(
              "min-w-7 px-0",
              index !== selectedInning &&
                "border-slate-300 bg-white text-slate-800 hover:bg-slate-100",
            )}
          >
            {index + 1}
          </Button>
        ))}
      </div>
      {inning ? (
        <>
          <h2 className="m-0 font-mono text-base font-bold tracking-[0.1em]">
            INNING {selectedInning + 1}
          </h2>
          <FieldDiagram
            labels={labels}
            tone="print"
            title={`Defensive positions for inning ${selectedInning + 1}`}
            className="mx-auto max-w-xl"
          />
          <div className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm">
            <strong>SITTING:</strong>{" "}
            {inning.sitters.length
              ? inning.sitters.map(nameOf).join(", ")
              : "full field"}
          </div>
        </>
      ) : null}
    </section>
  );
}

function MobileExport({
  defense,
  preferences,
  nameOf,
}: {
  defense: DefenseInningDocument[];
  preferences: Record<string, string[]>;
  nameOf: (id: string) => string;
}) {
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {defense.map((inning, index) => (
        <section
          key={index}
          className="overflow-hidden rounded-lg border-2 border-[#002b5c]"
        >
          <h2 className="m-0 flex items-center justify-between bg-[#002b5c] px-3 py-2 font-mono text-sm font-bold tracking-[0.1em] text-white">
            <span>INNING {index + 1}</span>
            {(inning.oop || []).length > 0 ? <span>⚠</span> : null}
          </h2>
          <div className="grid grid-cols-2 gap-x-3 p-2 text-sm">
            {POSITIONS.map((position) => {
              const playerId = inning.assignment[position];
              const isOop = isOutOfPosition(position, playerId, preferences);
              return (
                <div
                  key={position}
                  className={cn(
                    "flex min-w-0 gap-1.5 px-1 py-1",
                    isOop && "bg-amber-100",
                  )}
                >
                  <span className="w-7 shrink-0 font-mono font-bold">
                    {position}
                  </span>
                  <span
                    className={cn("min-w-0 break-words", isOop && "font-bold")}
                  >
                    {playerId ? nameOf(playerId) : "—"}
                    {isOop ? " ⚠" : ""}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="border-t border-red-200 bg-red-50 px-3 py-1.5 text-xs">
            <strong>SIT:</strong>{" "}
            {inning.sitters.length
              ? inning.sitters.map(nameOf).join(", ")
              : "full field"}
          </div>
        </section>
      ))}
    </div>
  );
}

function GridExport({
  defense,
  preferences,
  cell,
  nameOf,
}: {
  defense: DefenseInningDocument[];
  preferences: Record<string, string[]>;
  cell: (inning: DefenseInningDocument, position: string) => string;
  nameOf: (id: string) => string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="border border-slate-600 bg-slate-100 p-1.5">POS</th>
            {defense.map((_, index) => (
              <th
                key={index}
                className="border border-slate-600 bg-slate-100 p-1.5"
              >
                {index + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {POSITIONS.map((position) => (
            <tr key={position}>
              <th
                scope="row"
                className="border border-slate-600 p-1.5 text-left"
              >
                {position}
              </th>
              {defense.map((inning, index) => {
                const playerId = inning.assignment[position];
                const isOop = isOutOfPosition(position, playerId, preferences);
                return (
                  <td
                    key={index}
                    className={cn(
                      "whitespace-nowrap border border-slate-600 p-1.5",
                      isOop && "bg-amber-100 font-bold",
                    )}
                  >
                    {cell(inning, position)}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr>
            <th
              scope="row"
              className="border border-slate-600 bg-red-100 p-1.5 text-left"
            >
              SIT
            </th>
            {defense.map((inning, index) => (
              <td
                key={index}
                className="border border-slate-600 bg-red-50 p-1.5 text-[0.68rem]"
              >
                {inning.sitters.map(nameOf).join(", ")}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
